import { Icons, Tooltip } from '@commenda/kakashi';
import { cn } from '@commenda/utils';

interface ChatMessageActionButtonProps {
  onClick: () => void;
  tooltip: string;
  icon: React.ReactNode;
  active?: boolean;
}

const ChatMessageActionButton = ({
  onClick,
  tooltip,
  icon,
  active = false,
}: ChatMessageActionButtonProps) => {
  return (
    <Tooltip content={tooltip}>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex items-center p-sm rounded-sm',
          active
            ? 'bg-bg-primary-hover'
            : 'hover:bg-bg-primary-hover'
        )}
      >
        {icon}
      </button>
    </Tooltip>
  );
};

/**
 * Floating action bar that appears on message hover.
 * Positioned absolutely at top-0 left-0 of the message's relative container.
 */
const ChatMessageActionBar = ({
  onReply,
  onPin,
  isPinned,
}: {
  onReply: () => void;
  onPin: () => void;
  isPinned: boolean;
}) => {
  return (
    <div className="flex items-center gap-xs p-xs bg-bg-primary border border-border-secondary rounded-md shadow-xs">
      <ChatMessageActionButton
        onClick={onReply}
        tooltip="Reply to message"
        icon={<Icons.ReverseLeft className="size-3 text-fg-quaternary" />}
      />
      <ChatMessageActionButton
        onClick={onPin}
        tooltip={isPinned ? 'Unpin message' : 'Pin message'}
        icon={
          <Icons.Pin02
            className={cn(
              'size-3',
              isPinned ? 'text-fg-brand' : 'text-fg-quaternary'
            )}
          />
        }
        active={isPinned}
      />
    </div>
  );
};

const ReplyAction = ({ onClick }: { onClick: () => void }) => (
  <ChatMessageActionButton
    onClick={onClick}
    tooltip="Reply to message"
    icon={<Icons.CornerUpLeft className="size-3 text-fg-quaternary" />}
  />
);

const PinAction = ({
  onClick,
  isPinned,
}: {
  onClick: () => void;
  isPinned: boolean;
}) => (
  <ChatMessageActionButton
    onClick={onClick}
    tooltip={isPinned ? 'Unpin message' : 'Pin message'}
    icon={
      <Icons.Pin02
        className={cn(
          'size-3',
          isPinned ? 'text-fg-brand' : 'text-fg-quaternary'
        )}
      />
    }
    active={isPinned}
  />
);

const RemoveReplyAction = ({ onClick }: { onClick: () => void }) => (
  <ChatMessageActionButton
    onClick={onClick}
    tooltip="Remove reply"
    icon={<Icons.XClose className="size-3 text-fg-quaternary" />}
  />
);

export {
  ChatMessageActionBar,
  ChatMessageActionButton,
  ReplyAction,
  PinAction,
  RemoveReplyAction,
};
